import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MODAL_CONTROLLER, MODAL_DATA, ModalController } from 'shared';
import { map, tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { EquipmentMetaInfo } from '../../../services/installation-configuration.service';
import { UserTrackingHelperService } from '../../../services/user-tracking-helper.service';
import { ModalComponent } from '../../../../../../shared/src/lib/components/modal/modal.component';
import { ModalContentComponent } from '../../../../../../shared/src/lib/components/modal-content/modal-content.component';
import { ModalSectionComponent } from '../../../../../../shared/src/lib/components/modal-section/modal-section.component';
import { ModalSectionColumnComponent } from '../../../../../../shared/src/lib/components/modal-section-column/modal-section-column.component';
import { InputLabelComponent } from '../../../../../../shared/src/lib/components/input-label/input-label.component';
import { ToggleComponent } from '../../../../../../shared/src/lib/components/toggle/toggle.component';
import { InputFieldComponent } from '../../../../../../shared/src/lib/components/input-field/input-field.component';
import { DropdownComponent } from '../../../../../../shared/src/lib/components/dropdown/dropdown.component';
import { ModalButtonsComponent } from '../../../../../../shared/src/lib/components/modal-buttons/modal-buttons.component';
import { Angulartics2OnModule } from 'angulartics2';
import { ModalButtonComponent } from '../../../../../../shared/src/lib/components/modal-button/modal-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

export interface MixitWarmWeatherShutdownResponse {
  enabled: boolean;
  threshold: number;
  averagePeriod: number;
}

export interface MixitWarmWeatherShutdownSettings {
  enabled: boolean;
  threshold: number | null;
  averagePeriod: number | null;
  thresholdMeta: EquipmentMetaInfo;
}

@Component({
    selector: 'app-mixit-warm-weather-shutdown-modal',
    templateUrl: './mixit-warm-weather-shutdown-modal.component.html',
    styleUrls: ['./mixit-warm-weather-shutdown-modal.component.scss'],
    imports: [
        ModalComponent,
        ModalContentComponent,
        FormsModule,
        ReactiveFormsModule,
        ModalSectionComponent,
        ModalSectionColumnComponent,
        InputLabelComponent,
        ToggleComponent,
        InputFieldComponent,
        DropdownComponent,
        ModalButtonsComponent,
        Angulartics2OnModule,
        ModalButtonComponent,
        TranslateModule,
        AsyncPipe,
    ]
})
export class MixitWarmWeatherShutdownModalComponent implements OnInit, OnDestroy {
  public valid$: Observable<boolean>;
  public form: UntypedFormGroup;
  public thresholdMinMax: { min: number; max: number };
  public periods = [1, 2, 3];
  private subscription = new Subscription();

  constructor(
    @Inject(MODAL_DATA) public data: MixitWarmWeatherShutdownSettings,
    @Inject(MODAL_CONTROLLER) private controller: ModalController<MixitWarmWeatherShutdownResponse>,
    private userTrackingHelperService: UserTrackingHelperService
  ) {}

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      enabled: new UntypedFormControl(),
      threshold: new UntypedFormControl('', [
        Validators.required,
        Validators.min(this.data.thresholdMeta.min),
        Validators.max(this.data.thresholdMeta.max),
      ]),
      averagePeriod: new UntypedFormControl('', [Validators.required]),
    });

    this.thresholdMinMax = {
      min: this.data.thresholdMeta.min,
      max: this.data.thresholdMeta.max,
    };

    this.subscription.add(
      this.form.controls.enabled.valueChanges
        .pipe(
          tap((enabled) => {
            this.toggleEnabled(enabled);
          })
        )
        .subscribe()
    );

    this.valid$ = this.form.valueChanges.pipe(
      map((form) => {
        if (form.enabled) {
          return this.form.controls.threshold.valid && this.form.controls.averagePeriod.valid;
        }
        return true;
      })
    );

    this.toggleEnabled(this.data.enabled);

    setTimeout(() => {
      this.form.setValue({
        enabled: this.data.enabled,
        threshold: this.data.threshold,
        averagePeriod: this.data.averagePeriod,
      });

      this.subscription.add(this.userTrackingHelperService.startFormValueChangeTracking('adjustWarmWeatherShutdownShown', this.form));
    });
  }

  toggleEnabled(enabled: boolean) {
    if (enabled) {
      this.form.controls.threshold.enable();
      this.form.controls.averagePeriod.enable();
    } else {
      this.form.controls.threshold.disable();
      this.form.controls.averagePeriod.disable();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  close() {
    this.controller.dismiss();
  }

  save() {
    this.controller.complete({
      enabled: this.form.controls.enabled.value,
      threshold: this.form.controls.threshold.value,
      averagePeriod: this.form.controls.averagePeriod.value,
    });
  }

  dismiss() {
    this.userTrackingHelperService.trackUserAction('adjustWarmWeatherShutdownShown', 'exitClicked');
  }
}
